import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { Doc, Message, MessageAttachment, Sheet } from '@itsappy/shared';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
} from '@/components/ui/icons';
import { fileIconFor } from '@/components/ui/fileIcons';
import { Modal, Spinner } from '@/components/ui';
import { formatBytes, thumbnailUrl } from '@/lib/utils';
import { useOpenDoc, useOpenSheet } from '@/hooks/queries';
import { SheetWorkbook } from '@/components/sheet/SheetWorkbook';
import { DocSurface } from '@/components/doc/DocSurface';
import { VoiceNote } from './VoiceNote';

/** Anything that opens as an editable document rather than as a download. */
const isDoc = (file: MessageAttachment) =>
  file.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
  file.mimeType === 'application/msword' ||
  file.mimeType === 'text/markdown' ||
  /\.(docx|doc|md|markdown)$/i.test(file.name);

/** Anything that opens as a spreadsheet — including the Excel formats the
 *  old CSV-only preview could never read. */
const isSheet = (file: MessageAttachment) =>
  file.mimeType === 'text/csv' ||
  file.mimeType === 'text/tab-separated-values' ||
  file.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
  file.mimeType === 'application/vnd.ms-excel' ||
  /\.(csv|tsv|xlsx|xlsm|xls)$/i.test(file.name);

const isPdf = (file: MessageAttachment) =>
  file.mimeType === 'application/pdf' || /\.pdf$/i.test(file.name);

interface GalleryValue {
  /**
   * Opens the viewer on the picture with this attachment id. Returns false when
   * the id is not in the gallery — the caller then shows that one picture on
   * its own.
   */
  open: (attachmentId: string) => boolean;
}

const ImageGalleryContext = createContext<GalleryValue | null>(null);

/** The attachments that belong in the viewer: pictures, minus the stickers and
 *  GIFs that are drawn as speech rather than as files. */
const isGalleryImage = (file: MessageAttachment) =>
  file.mimeType.startsWith('image/') && file.kind !== 'sticker' && file.kind !== 'gif';

/**
 * Collects a conversation's pictures into one gallery, so opening the third of
 * twelve photos opens at the third and the rest are an arrow key or a swipe
 * away rather than a scroll back through the transcript.
 */
export function ImageGalleryProvider({
  messages,
  children,
}: {
  /** The transcript, straight from the query — held by reference so the
   *  gallery is only rebuilt when the messages themselves change. */
  messages: Message[] | undefined;
  children: ReactNode;
}) {
  // The open picture is held as an id rather than an index: loading older
  // messages prepends to the gallery, which would slide an index onto a
  // different photo mid-view.
  const [openId, setOpenId] = useState<string | null>(null);

  // Oldest first — the order the pictures are paged through.
  const images = useMemo(
    () => (messages ?? []).flatMap((message) => message.attachments.filter(isGalleryImage)),
    [messages],
  );

  const value = useMemo<GalleryValue>(
    () => ({
      open: (attachmentId) => {
        if (!images.some((image) => image.id === attachmentId)) return false;
        setOpenId(attachmentId);
        return true;
      },
    }),
    [images],
  );

  const index = openId === null ? -1 : images.findIndex((image) => image.id === openId);

  return (
    <ImageGalleryContext.Provider value={value}>
      {children}
      {index !== -1 && (
        <Lightbox
          files={images}
          index={index}
          onIndexChange={(next) => setOpenId(images[next].id)}
          onClose={() => setOpenId(null)}
        />
      )}
    </ImageGalleryContext.Provider>
  );
}

export interface ChatAttachmentsProps {
  attachments: MessageAttachment[];
  /** Asks the server to transcribe a voice note; omitted when unavailable. */
  onTranscribe?: (attachment: MessageAttachment) => void;
  transcribingId?: string;
}

/** Renders the attachments carried by a message in the transcript. */
export function ChatAttachments({
  attachments,
  onTranscribe,
  transcribingId,
}: ChatAttachmentsProps) {
  const gallery = useContext(ImageGalleryContext);
  const [preview, setPreview] = useState<MessageAttachment | null>(null);

  if (!attachments.length) return null;
  return (
    <>
      <div className="mt-1.5 space-y-2">
        {attachments.map((file) => {
          /* A sticker is a picture with no chrome — no name, no byte count,
             no lightbox. Treating it like an uploaded PNG makes it read as a
             file someone sent rather than a thing they said. */
          if (file.kind === 'sticker') {
            return (
              <img
                key={file.id}
                src={file.url}
                alt={file.name}
                loading="lazy"
                className="h-28 w-28 object-contain"
              />
            );
          }

          if (file.kind === 'gif') {
            return (
              <figure key={file.id} className="w-fit max-w-full">
                <img
                  src={file.url}
                  alt={file.name}
                  loading="lazy"
                  className="max-h-64 max-w-full rounded-xl object-contain"
                />
                <figcaption className="pt-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                  GIF
                </figcaption>
              </figure>
            );
          }

          if (file.kind === 'voice' || file.mimeType.startsWith('audio/')) {
            return (
              <VoiceNote
                key={file.id}
                attachment={file}
                onTranscribe={onTranscribe ? () => onTranscribe(file) : undefined}
                transcribing={transcribingId === file.id}
              />
            );
          }

          if (file.mimeType.startsWith('image/')) {
            return (
              <button
                type="button"
                key={file.id}
                /* Inside a conversation the viewer opens on the whole gallery so
                   the other pictures are one arrow or swipe away; on its own it
                   shows just this one. */
                onClick={() => {
                  if (!gallery?.open(file.id)) setPreview(file);
                }}
                aria-label={`Preview ${file.name}`}
                className="block w-fit max-w-full cursor-zoom-in rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                {/* The intrinsic size lets the browser reserve the right box
                    before the picture decodes, so a transcript of photos
                    stops jumping as they load in. */}
                <img
                  src={thumbnailUrl(file.url)}
                  alt={file.name}
                  width={file.width}
                  height={file.height}
                  loading="lazy"
                  decoding="async"
                  className="max-h-[min(18rem,38dvh)] max-w-full rounded-xl object-contain sm:max-h-64"
                />
              </button>
            );
          }

          if (file.mimeType.startsWith('video/')) {
            return <VideoAttachment key={file.id} file={file} />;
          }

          if (isPdf(file)) {
            return (
              <PdfAttachment key={file.id} file={file} onExpand={() => setPreview(file)} />
            );
          }

          if (isSheet(file)) {
            return <SheetAttachment key={file.id} file={file} />;
          }

          if (isDoc(file)) {
            return <DocAttachment key={file.id} file={file} />;
          }

          const Icon = fileIconFor(file.mimeType);
          return (
            <a
              key={file.id}
              href={file.url}
              target="_blank"
              rel="noreferrer noopener"
              className="flex w-fit max-w-full items-center gap-2 rounded-lg bg-surface-3 px-2 py-1.5 text-xs text-slate-200 transition-colors hover:bg-surface-4"
            >
              <Icon size="sm" />
              <span className="truncate">{file.name}</span>
              <span className="shrink-0 text-slate-400">{formatBytes(file.size)}</span>
            </a>
          );
        })}
      </div>
      {preview && (
        <Lightbox
          files={[preview]}
          index={0}
          onIndexChange={() => {}}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

/**
 * Video plays in place, in a box of its own shape. `preload="metadata"` so a
 * channel full of clips costs a few kilobytes of headers rather than
 * downloading every one of them — and it is that same metadata the box takes
 * its shape from, once it arrives.
 */
function VideoAttachment({ file }: { file: MessageAttachment }) {
  const [box, setBox] = useState(() => videoBox(file.width, file.height));

  return (
    <figure className="max-w-full" style={{ width: box.width }}>
      {/* The shape lives on the frame, not on the <video>: `aspect-ratio` on a
          replaced element does not reliably give it a height, and a video with
          no height is the zero-pixel sliver this started as. The frame holds
          the box open and the player fills it. */}
      <div
        className="relative w-full overflow-hidden rounded-xl bg-black"
        style={{ aspectRatio: box.aspect }}
      >
        <video
          controls
          /* iOS Safari otherwise yanks any play into its own full-screen
             player, losing the transcript behind it. */
          playsInline
          preload="metadata"
          src={file.url}
          aria-label={file.name}
          onLoadedMetadata={(event) => {
            const { videoWidth, videoHeight } = event.currentTarget;
            if (videoWidth > 0 && videoHeight > 0) setBox(videoBox(videoWidth, videoHeight));
          }}
          onPlay={(event) => {
            document
              .querySelectorAll<HTMLVideoElement>('video[data-chat-video]')
              .forEach((other) => {
                if (other !== event.currentTarget && !other.paused) other.pause();
              });
          }}
          data-chat-video
          className="absolute inset-0 h-full w-full object-contain"
        />
      </div>
      <figcaption className="truncate pt-0.5 text-[11px] text-slate-500">
        {file.name} · {formatBytes(file.size)}
      </figcaption>
    </figure>
  );
}

const VIDEO_BOX_WIDTH = 352;
const VIDEO_BOX_HEIGHT = 320;
/* The same limits the picture bubbles use: a letterbox panorama would render
   as an unreadable sliver, and a phone-shot portrait clip at its own ratio
   would push the rest of the conversation off screen. Past these the box stops
   following the clip and letterboxes it instead. */
const MIN_VIDEO_ASPECT = 0.62;
const MAX_VIDEO_ASPECT = 2.1;

/**
 * The box a clip gets, in the shape it will actually be shown at.
 *
 * Uploads carry no dimensions for video — the server measures images only — so
 * this opens at the shape most clips are and is called again with the real
 * numbers once `loadedmetadata` lands. Reserving the box up front is what
 * stops the transcript jumping as each clip loads.
 *
 * A concrete pixel width rather than a percentage: these sit inside a
 * shrink-to-fit message bubble, where a percentage width has nothing
 * determinate to resolve against and collapses the frame to nothing.
 */
function videoBox(width?: number, height?: number): { width: number; aspect: number } {
  const raw = width && height && width > 0 && height > 0 ? width / height : 16 / 9;
  const aspect = Math.min(Math.max(raw, MIN_VIDEO_ASPECT), MAX_VIDEO_ASPECT);
  const tall = VIDEO_BOX_WIDTH / aspect > VIDEO_BOX_HEIGHT;
  return { width: tall ? Math.round(VIDEO_BOX_HEIGHT * aspect) : VIDEO_BOX_WIDTH, aspect };
}

/**
 * PDFs render in a collapsed strip that expands into an inline viewer, rather
 * than mounting an iframe per PDF the moment a channel loads.
 */
function PdfAttachment({ file, onExpand }: { file: MessageAttachment; onExpand: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="w-full max-w-md overflow-hidden rounded-xl ring-1 ring-surface-3">
      <div className="flex items-center gap-2 bg-surface-2 px-2.5 py-2 text-xs">
        <span aria-hidden className="text-base leading-none">
          📄
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-slate-200">{file.name}</span>
          <span className="block text-[11px] text-slate-500">
            PDF · {formatBytes(file.size)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-brand-300 transition-colors hover:bg-surface-3"
        >
          {open ? 'Hide' : 'Preview'}
        </button>
        <button
          type="button"
          onClick={onExpand}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          Full screen
        </button>
      </div>
      {open && (
        <iframe
          src={`${file.url}#toolbar=0&navpanes=0`}
          title={file.name}
          className="h-80 w-full border-0 bg-white"
        />
      )}
    </div>
  );
}

/**
 * A spreadsheet attachment, opened as the live shared grid rather than as a
 * download.
 *
 * The old preview here parsed the first 64KB as CSV, which meant it could
 * show a comma-separated file and nothing else — an `.xlsx` is a zip archive,
 * so reading it as text produced noise. Opening the real workbook shows every
 * format, all of the rows rather than the first eight, and puts the two people
 * discussing the numbers in the same document instead of each in their own
 * copy of the download.
 */
function SheetAttachment({ file }: { file: MessageAttachment }) {
  const [open, setOpen] = useState(false);
  const openSheet = useOpenSheet();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [error, setError] = useState<string | null>(null);

  const request = openSheet.mutateAsync;
  useEffect(() => {
    if (!open || sheet || error) return;
    let live = true;
    void request({ fileId: file.id })
      .then((opened) => {
        if (live) setSheet(opened);
      })
      .catch((cause: unknown) => {
        if (live) {
          setError(cause instanceof Error ? cause.message : 'This sheet could not be opened');
        }
      });
    return () => {
      live = false;
    };
  }, [open, sheet, error, file.id, request]);

  return (
    <>
      <div className="flex w-full max-w-md items-center gap-2 overflow-hidden rounded-xl bg-surface-2 px-2.5 py-2 text-xs ring-1 ring-surface-3">
        <span aria-hidden className="text-base leading-none">
          📊
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-slate-200">{file.name}</span>
          <span className="block text-[11px] text-slate-500">
            Spreadsheet · {formatBytes(file.size)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-brand-300 transition-colors hover:bg-surface-3"
        >
          Open
        </button>
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer noopener"
          download={file.name}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          Download
        </a>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={file.name} width="xl">
        {error ? (
          <p className="px-4 py-10 text-center text-sm text-red-400">{error}</p>
        ) : !sheet ? (
          <div className="grid h-64 place-items-center">
            <Spinner className="h-6 w-6" />
          </div>
        ) : (
          <SheetWorkbook
            sheetId={sheet.id}
            name={sheet.name}
            canManage={sheet.canManage}
            className="h-[65dvh] w-full overflow-hidden rounded-xl bg-surface-2"
          />
        )}
      </Modal>
    </>
  );
}

/**
 * A document attachment, opened as the live shared editor rather than as a
 * download — so two people discussing a proposal in chat can write in it
 * together instead of mailing versions back and forth.
 */
function DocAttachment({ file }: { file: MessageAttachment }) {
  const [open, setOpen] = useState(false);
  const openDoc = useOpenDoc();
  const [doc, setDoc] = useState<Doc | null>(null);
  const [error, setError] = useState<string | null>(null);

  const request = openDoc.mutateAsync;
  useEffect(() => {
    if (!open || doc || error) return;
    let live = true;
    void request({ fileId: file.id })
      .then((opened) => {
        if (live) setDoc(opened);
      })
      .catch((cause: unknown) => {
        if (live) {
          setError(cause instanceof Error ? cause.message : 'This document could not be opened');
        }
      });
    return () => {
      live = false;
    };
  }, [open, doc, error, file.id, request]);

  return (
    <>
      <div className="flex w-full max-w-md items-center gap-2 overflow-hidden rounded-xl bg-surface-2 px-2.5 py-2 text-xs ring-1 ring-surface-3">
        <span aria-hidden className="text-base leading-none">
          📄
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-slate-200">{file.name}</span>
          <span className="block text-[11px] text-slate-500">
            Document · {formatBytes(file.size)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-brand-300 transition-colors hover:bg-surface-3"
        >
          Open
        </button>
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer noopener"
          download={file.name}
          className="shrink-0 rounded-md px-2 py-1 font-medium text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          Download
        </a>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={file.name} width="xl">
        {error ? (
          <p className="px-4 py-10 text-center text-sm text-red-400">{error}</p>
        ) : !doc ? (
          <div className="grid h-64 place-items-center">
            <Spinner className="h-6 w-6" />
          </div>
        ) : (
          <DocSurface
            docId={doc.id}
            name={doc.title}
            className="h-[65dvh] w-full overflow-hidden rounded-xl bg-surface-2"
          />
        )}
      </Modal>
    </>
  );
}

/** How far a finger has to travel before it counts as a page turn rather than
 *  a tap that happened to wobble. */
const SWIPE_THRESHOLD = 48;

/**
 * Full-screen viewer for images and PDFs. Given more than one file it pages:
 * arrow keys, the buttons either side, or a swipe walk the gallery without
 * closing and reopening the viewer.
 */
function Lightbox({
  files,
  index,
  onIndexChange,
  onClose,
}: {
  files: MessageAttachment[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const file = files[index];
  const many = files.length > 1;

  const step = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < files.length) onIndexChange(next);
    },
    [index, files.length, onIndexChange],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, step]);

  // Touch is tracked by hand rather than with a scroll-snap strip: the viewer
  // holds full-size images, and a strip would fetch every one of them.
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (event: React.TouchEvent) => {
    const point = event.touches[0];
    touch.current = { x: point.clientX, y: point.clientY };
  };
  const onTouchEnd = (event: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const point = event.changedTouches[0];
    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    // A mostly-vertical drag is a dismiss gesture elsewhere, not a page turn.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    step(dx < 0 ? 1 : -1);
  };

  const pdf = isPdf(file);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/80 p-3 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`${pdf ? 'Document' : 'Image'} preview: ${file.name}`}
      onTouchStart={many ? onTouchStart : undefined}
      onTouchEnd={many ? onTouchEnd : undefined}
    >
      <button
        type="button"
        className="absolute inset-0 cursor-zoom-out"
        onClick={onClose}
        aria-label="Close preview"
      />
      <figure className="relative z-10 flex max-h-full w-full max-w-4xl flex-col items-center gap-2">
        {pdf ? (
          <iframe
            src={file.url}
            title={file.name}
            className="h-[calc(100dvh-8rem)] w-full rounded-xl border-0 bg-white shadow-2xl"
          />
        ) : (
          <img
            src={file.url}
            alt={file.name}
            className="max-h-[calc(100dvh-6rem)] max-w-[calc(100vw-1.5rem)] rounded-xl object-contain shadow-2xl sm:max-w-[calc(100vw-4rem)]"
          />
        )}
        <figcaption className="flex max-w-[min(40rem,calc(100vw-4rem))] items-center gap-2 text-sm text-white/80">
          <span className="truncate">{file.name}</span>
          {many && (
            <span className="shrink-0 text-white/50">
              {index + 1} of {files.length}
            </span>
          )}
        </figcaption>
      </figure>

      {/* Swiping is the gesture on a phone; the buttons say so out loud, and
          give a mouse and a keyboard something to hit. */}
      {many && (
        <>
          <Step
            side="left"
            disabled={index === 0}
            onClick={() => step(-1)}
            label="Previous image"
          />
          <Step
            side="right"
            disabled={index === files.length - 1}
            onClick={() => step(1)}
            label="Next image"
          />
          {/* The neighbours are fetched while this one is being looked at, so a
              swipe lands on a picture rather than on a blank frame. */}
          {[files[index - 1], files[index + 1]].map(
            (neighbour) =>
              neighbour && (
                <img key={neighbour.id} src={neighbour.url} alt="" className="hidden" />
              ),
          )}
        </>
      )}

      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-20 grid h-11 w-11 place-items-center rounded-xl bg-black/60 text-white transition-colors hover:bg-black/80 sm:right-6 sm:top-6"
      >
        <CloseIcon size="lg" />
      </button>
    </div>,
    document.body,
  );
}

function Step({
  side,
  disabled,
  onClick,
  label,
}: {
  side: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
  label: string;
}) {
  const Icon = side === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`absolute top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80 disabled:pointer-events-none disabled:opacity-25 ${
        side === 'left' ? 'left-2 sm:left-6' : 'right-2 sm:right-6'
      }`}
    >
      <Icon size="lg" />
    </button>
  );
}
